import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TenantAdminDashboard from './pages/TenantAdminDashboard';
import VendorDashboard from './pages/VendorDashboard';
import AuditorDashboard from './pages/AuditorDashboard';
import BatchCreate from './pages/BatchCreate';
import BatchDetails from './pages/BatchDetails';
import ImageUpload from './pages/ImageUpload';
import ValidationResults from './pages/ValidationResults';
import BatchRework from './pages/BatchRework';
import BatchReview from './pages/BatchReview';
import Users from './pages/Users';
import TenantManagement from './pages/TenantManagement';
import AdminUserManagement from './pages/AdminUserManagement';
import JobManagement from './pages/JobManagement';
import BatchList from './pages/BatchList';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Admin-only Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'admin') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <svg style={{ width: '64px', height: '64px', marginBottom: '20px', color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>
            Access Denied
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '30px' }}>
            You don't have permission to access this page. Admin access required.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Auditor-only Route Component
const AuditorRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'auditor') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Smart Home Redirect based on role and tenant_id
const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect based on role and tenant_id
  if (user.role === 'auditor') {
    return <Navigate to="/auditor" />;
  }

  if (user.role === 'admin') {
    // Super Admin (tenant_id = null) goes to super admin dashboard
    if (user.tenant_id === null) {
      return <Navigate to="/super-admin" />;
    }
    // Tenant Admin (tenant_id = 1,2,3...) goes to tenant admin dashboard
    return <Navigate to="/tenant-admin" />;
  }

  // Vendor users go to vendor dashboard
  if (user.role === 'vendor_user') {
    return <Navigate to="/vendor" />;
  }

  return <Navigate to="/login" />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Route */}
          <Route path="/login" element={<Login />} />

          {/* Super Admin Dashboard */}
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Tenant Admin Dashboard */}
          <Route
            path="/tenant-admin"
            element={
              <ProtectedRoute>
                <TenantAdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Vendor Dashboard */}
          <Route
            path="/vendor"
            element={
              <ProtectedRoute>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />

          {/* Auditor Dashboard */}
          <Route
            path="/auditor"
            element={
              <AuditorRoute>
                <AuditorDashboard />
              </AuditorRoute>
            }
          />

          {/* Legacy Dashboard Route - Redirect to appropriate dashboard */}
          <Route path="/dashboard" element={<HomeRedirect />} />

          {/* Admin Only - Tenant Management */}
          <Route
            path="/admin/tenants"
            element={
              <AdminRoute>
                <TenantManagement />
              </AdminRoute>
            }
          />

          {/* Admin Only - User Management */}
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUserManagement />
              </AdminRoute>
            }
          />

          {/* Admin Only - Job Management */}
          <Route
            path="/admin/jobs"
            element={
              <AdminRoute>
                <JobManagement />
              </AdminRoute>
            }
          />

          {/* Admin Only - Old User Management (Keep for backward compatibility) */}
          <Route
            path="/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />

          {/* Batch List */}
          <Route
            path="/batches"
            element={
              <ProtectedRoute>
                <BatchList />
              </ProtectedRoute>
            }
          />

          {/* Batch Management */}
          <Route
            path="/batch/create"
            element={
              <ProtectedRoute>
                <BatchCreate />
              </ProtectedRoute>
            }
          />

          <Route
            path="/batch/:batchId"
            element={
              <ProtectedRoute>
                <BatchDetails />
              </ProtectedRoute>
            }
          />

          {/* Image Upload */}
          <Route
            path="/batch/:batchId/upload"
            element={
              <ProtectedRoute>
                <ImageUpload />
              </ProtectedRoute>
            }
          />

          {/* Vendor - Rework Failed Images */}
          <Route
            path="/batch/:batchId/rework"
            element={
              <ProtectedRoute>
                <BatchRework />
              </ProtectedRoute>
            }
          />

          {/* Auditor - Review & Approve */}
          <Route
            path="/batch/:batchId/review"
            element={
              <ProtectedRoute>
                <BatchReview />
              </ProtectedRoute>
            }
          />

          {/* Validation Results */}
          <Route
            path="/image/:imageId/validation"
            element={
              <ProtectedRoute>
                <ValidationResults />
              </ProtectedRoute>
            }
          />

          {/* Default Redirect - Smart redirect based on user role */}
          <Route path="/" element={<HomeRedirect />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;